// Facility landing pages (COM-773/774) — one page per Google Ads campaign,
// served at /kitchens/<slug> to match the ads' display URLs. Each entry pairs
// a kitchen from the shared dataset (or a standalone definition) with the
// ad-matched headline/subline so the landing page answers the exact promise
// the ad made.
import { Equipment, Kitchen, Service, kitchens, v2FacilityIdFor } from './kitchens';

export interface FacilityPage {
  slug: string;
  /**
   * The `kitchens.ts` id this page describes, when there is one. Present so a
   * kitchen can find its landing page without a second copy of the mapping
   * living in a component (CMSY-75). Optional because a landing page need not
   * name a kitchen in the dataset, which is why `facilitySlugForKitchen` may
   * return undefined and every caller must handle it.
   */
  kitchenId?: string;
  /** The v2 intake API's facility id — preselects this kitchen on /application. */
  v2FacilityId?: number;
  name: string;
  headline: string;
  subline: string;
  address: string;
  image: string;
  status: 'operating' | 'coming-soon';
  size?: string;
  capacity?: string;
  features: string[];
  idealFor: string[];
  kitchenEquipment: Equipment[];
  includedServices: Service[];
  ctaText: string;
  ctaLabel: string;
  seoDescription: string;
  /** Optional <title>; falls back to `${name} | Commissary Connect`. Query phrase first (CMSY-32). */
  seoTitle?: string;
}

function byId(id: string): Kitchen {
  const kitchen = kitchens.find((k) => k.id === id);
  if (!kitchen) throw new Error(`facilityPages: unknown kitchen id "${id}"`);
  return kitchen;
}

function fromKitchen(k: Kitchen) {
  return {
    // Carried through so the kitchen → slug lookup below needs no hand-written
    // second copy of the pairing (CMSY-75).
    kitchenId: k.id,
    // The v2 facility id comes from the one mapping in kitchens.ts (CMSY-28) —
    // never retyped here, where a typo would book the wrong kitchen.
    v2FacilityId: v2FacilityIdFor(k.id),
    name: k.name,
    address: k.address,
    image: k.images?.[0] ?? '/Commissary-Connect-1.png',
    status: k.status,
    size: k.size,
    capacity: k.capacity,
    features: k.features,
    idealFor: k.idealFor,
    kitchenEquipment: k.kitchenEquipment,
    includedServices: k.includedServices,
  };
}

export const FACILITIES: Record<string, FacilityPage> = {
  kitsilano: {
    ...fromKitchen(byId('vancouver-kitsilano')),
    slug: 'kitsilano',
    // The facility's current name — the dataset still carries retired partner
    // branding that must not surface on the ad landing page.
    name: 'Commissary Connect Kitsilano',
    features: [
      'Pay-Per-Use Hourly Booking',
      'Business Accelerator',
      'Prime Kitsilano Location',
      'Culinary Community',
    ],
    headline: 'Book a Licensed Catering Kitchen by the Hour in Kitsilano',
    subline:
      'Pay only for the hours you cook. Fully equipped, health-authority-licensed commercial stations in the heart of Kits — no lease, no build-out, no long-term commitment.',
    ctaText: 'Apply for Kitchen Space',
    ctaLabel: 'facility_kitsilano',
    seoTitle: 'Shared Commercial Kitchen in Kitsilano | Rent by the Hour',
    seoDescription:
      'Rent a licensed commercial catering kitchen by the hour in Kitsilano, Vancouver. Pay-per-use stations, professional equipment, 24-hour access. Apply today.',
  },
  '401-industrial': {
    ...fromKitchen(byId('vancouver-401')),
    slug: '401-industrial',
    headline: 'Your Food Truck’s Home Base: Volume Production Kitchen at 401 Industrial',
    subline:
      'Vancouver’s original commissary kitchen, minutes from downtown. 18 stations and 6 convection ovens built for high-volume production — food trucks, caterers, and ghost kitchens have scaled here since 2012.',
    ctaText: 'Apply for Kitchen Space',
    ctaLabel: 'facility_401-industrial',
    seoTitle: 'Industrial Kitchen for Rent | 401 Industrial Ave, Vancouver',
    seoDescription:
      'Industrial kitchen for rent at 401 Industrial Ave, Vancouver — 18 stations, 6 ovens, heavy-duty ventilation. Home base for food trucks and caterers. Apply.',
  },
  'laurel-haccp': {
    ...fromKitchen(byId('vancouver-laurel')),
    slug: 'laurel-haccp',
    // "HACCP-Ready", never "HACCP-Certified". The facility is built to HACCP
    // standards and supports a member's own certification; it does not hold a
    // certification it can extend to them, and saying otherwise is a claim we
    // cannot support. This line said "Certified" from launch (2026-08-29) while
    // the subline, the SEO description, kitchens.ts, Features.tsx and Story.tsx
    // all said "ready" — one word out of step, on the page both Laurel ads land
    // on. Found by Jeffrey 2026-09-22.
    headline: 'HACCP-Ready Production & Co-Packing Kitchen in Vancouver',
    subline:
      'Western Canada’s only shared-use HACCP-ready facility. Produce and co-pack for major retailers with a quality control lab, packaging equipment, and cold chain storage behind you.',
    ctaText: 'Apply for Kitchen Space',
    ctaLabel: 'facility_laurel-haccp',
    seoDescription:
      'HACCP-ready shared production and co-packing kitchen at 8811 Laurel St, Vancouver. QC lab, packaging equipment, cold chain storage for CPG brands.',
  },
  'laurel-prep': {
    ...fromKitchen(byId('vancouver-laurel-2nd')),
    slug: 'laurel-prep',
    headline: 'Licensed Prep & Finishing Kitchen Stations in South Vancouver',
    subline:
      'Light prep and finishing without the overhead. VCH-approved stations with commercial ranges, convection ovens, and a walk-in cooler — the affordable way to start producing.',
    ctaText: 'Apply for Kitchen Space',
    ctaLabel: 'facility_laurel-prep',
    seoDescription:
      'Licensed light-prep and finishing kitchen stations at 8811 Laurel St, Vancouver. VCH-approved, fully equipped, entry-level pricing. Apply for a station.',
  },
  '417-industrial': {
    slug: '417-industrial',
    // Set by hand because this entry is standalone rather than built by
    // fromKitchen. The dataset calls the same address "Terminal Vancouver";
    // see CLAUDE.md §Gotchas — one address, two names, reconcile when branding
    // is decided.
    kitchenId: 'vancouver-terminal',
    v2FacilityId: v2FacilityIdFor('vancouver-terminal'),
    name: '417 Industrial — Baking Kitchen',
    address: '417 Industrial Ave, Vancouver, BC',
    image: '/417.webp',
    status: 'operating',
    capacity: byId('vancouver-terminal').capacity,
    headline: 'Vancouver’s Shared Baking Kitchen — Join the Baker Waitlist',
    subline:
      'A bakery-focused shared kitchen on Industrial Ave: 9 stations with 5 commercial ovens, built for bakers, pastry chefs, and confectioners. Stations fill fast — join the waitlist for the next opening.',
    features: ['Bakery-Focused Shared Kitchen', '5 Commercial Ovens', 'Pop-up Retail Space', 'Member Events'],
    idealFor: ['Bakery & Pastry', 'Confectionery', 'Catering & Events', 'Startup & Small Batch'],
    kitchenEquipment: [
      { name: 'Convection Ovens', icon: '/icons/kitchen/convection-oven.svg' },
      { name: 'Steamer', icon: '/icons/kitchen/steamer.svg' },
      { name: 'Walk-in Cooler', icon: '/icons/kitchen/cooler.svg' },
      { name: 'Dishwasher', icon: '/icons/kitchen/dishwasher.svg' },
    ],
    includedServices: [
      { name: 'Cleaning Supplies', icon: '/icons/kitchen/cleaning.svg' },
      { name: 'Equipment Maintenance', icon: '/icons/kitchen/maintenance.svg' },
      { name: 'Security Cameras', icon: '/icons/kitchen/security.svg' },
      { name: 'Waste Disposal', icon: '/icons/kitchen/waste.svg' },
      { name: 'WiFi', icon: '/icons/kitchen/wifi.svg' },
      { name: 'Utilities', icon: '/icons/kitchen/utilities.svg' },
      { name: 'Pest Control', icon: '/icons/kitchen/pest-control.svg' },
      { name: '24 Hour Access', icon: '/icons/kitchen/24-hour.svg' },
      { name: 'Online Booking', icon: '/icons/kitchen/online-booking.svg' },
    ],
    ctaText: 'Join the Baker Waitlist',
    ctaLabel: 'facility_417-industrial',
    seoTitle: 'Industrial Baking Kitchen for Rent | 417 Industrial Ave',
    seoDescription:
      'Bakery-focused industrial kitchen for rent at 417 Industrial Ave, Vancouver — 9 stations, 5 commercial ovens for bakers and confectioners. Join waitlist.',
  },
};

/**
 * The facility landing page for a kitchen, or `undefined` when that kitchen has
 * none — the sibling operators' kitchens (Fraser Valley, Surrey, PurePrep) do
 * not, and never should.
 *
 * Callers link only when this returns a slug. Never fall back to a default: the
 * same shortcut in `v2FacilityIdFor` once sent every unknown kitchen to Laurel
 * HACCP, and here it would point a card at another kitchen's landing page.
 *
 * Exists because `FacilityTiles` was the only internal link to /kitchens/<slug>
 * anywhere on the site, and removing it from the homepage on 2026-09-11 left
 * all six pages orphaned (CMSY-75).
 */
export function facilitySlugForKitchen(kitchenId: string): string | undefined {
  return Object.values(FACILITIES).find((f) => f.kitchenId === kitchenId)?.slug;
}
